import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/hooks/useAuth";

/**
 * PRIV-2 — the login-time MFA step. Rendered by AuthPage (the only login surface, master-admin included)
 * when a login is paused for a second factor (`pendingMfa` is set). Two modes:
 *   - "challenge": the account has MFA enabled → prompt for a TOTP or recovery code.
 *   - "enroll": the role REQUIRES MFA but the account isn't enrolled → show the secret to add to an
 *     authenticator, verify the first code, and reveal one-time recovery codes before continuing.
 *
 * Enrollment shows a QR of the otpauth:// URI (rendered client-side with qrcode.react as inline SVG —
 * CSP-safe, no network) plus the manual key for authenticators that can't scan. The URI is encoded into
 * the QR only; it is never rendered as text (priv-2-fix2).
 *
 * priv-2-fix: the form uses a LOCAL submitting/cooldown model (not the shared react-query isPending
 * flags) so a failed verify always re-enables the input, refocuses it, and clears the field — the user
 * can retry immediately. A 429 shows a countdown and re-enables when it elapses. Cancel always works.
 */
function extractRetryAfter(err) {
  const body = err?.response?.data?.retry_after;
  const header = err?.response?.headers?.["retry-after"];
  const n = Number(body ?? header);
  return Number.isFinite(n) && n > 0 ? Math.ceil(n) : 30;
}

export function MfaPanel() {
  const { t } = useTranslation();
  const {
    pendingMfa,
    clearPendingMfa,
    mfaChallengeAsync,
    mfaForcedEnrollStartAsync,
    mfaForcedEnrollCompleteAsync,
  } = useAuth();

  const [code, setCode] = useState("");
  const [enrollSecret, setEnrollSecret] = useState(null); // { secret, otpauth_uri }
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0); // seconds remaining after a 429
  const inputRef = useRef(null);

  const mode = pendingMfa?.mode;

  useEffect(() => {
    // On entering forced-enroll mode, fetch the secret to display.
    let active = true;
    if (mode === "enroll" && !enrollSecret) {
      mfaForcedEnrollStartAsync()
        .then((res) => { if (active) setEnrollSecret(res?.data || null); })
        .catch(() => { if (active) setError(t("auth.mfaEnrollFailed")); });
    }
    return () => { active = false; };
  }, [mode, enrollSecret, mfaForcedEnrollStartAsync, t]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  if (!pendingMfa) return null;

  // priv-2-fix — defensive guard: if a login returned an MFA requirement this surface cannot service
  // (missing token, or an unrecognised mode), show a clear message and stop. Never silently retry the
  // login (which would loop on the same mfa_required response). This is the same panel the master-admin
  // login uses, so the guard covers every login surface.
  if (!pendingMfa.mfaToken || (mode !== "challenge" && mode !== "enroll")) {
    return (
      <div className="space-y-4" data-testid="mfa-unavailable">
        <h2 className="text-center text-lg font-semibold text-ink">{t("auth.mfaUnavailableTitle")}</h2>
        <p className="text-sm text-slate-600">{t("auth.mfaUnavailableBody")}</p>
        <button
          type="button"
          onClick={() => { clearPendingMfa(); setCode(""); }}
          className="w-full rounded-lg bg-primary py-2 font-semibold text-white hover:bg-primary-deep"
        >
          {t("auth.mfaCancel")}
        </button>
      </div>
    );
  }

  const busy = submitting || cooldown > 0;

  // Shared submit: on error ALWAYS re-enable + refocus + clear the field so retry is possible. Returns
  // the result on success (caller handles it), or null on failure.
  const runSubmit = async (fn) => {
    setError("");
    setSubmitting(true);
    try {
      return await fn({ code });
    } catch (err) {
      if (err?.response?.status === 429) {
        setCooldown(extractRetryAfter(err));
      } else {
        setError(t("auth.mfaCodeInvalid"));
      }
      setCode("");
      requestAnimationFrame(() => inputRef.current?.focus());
      return null;
    } finally {
      setSubmitting(false); // the pending flag is reset in EVERY path (this was the stuck-disabled bug)
    }
  };

  const onChallenge = async (e) => {
    e.preventDefault();
    await runSubmit(mfaChallengeAsync); // success → finalizeSession clears pendingMfa and the app moves on
  };

  const onEnrollComplete = async (e) => {
    e.preventDefault();
    const res = await runSubmit(mfaForcedEnrollCompleteAsync);
    const codes = res?.data?.recovery_codes;
    if (codes && codes.length) setRecoveryCodes(codes);
  };

  const codeInput = (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      // Accept a 6-digit TOTP AND a recovery code (xxxxx-xxxxx). maxLength fits the longer recovery form
      // so recovery entry is never truncated; TOTP is well within it.
      maxLength={14}
      value={code}
      onChange={(e) => setCode(e.target.value)}
      disabled={busy}
      placeholder={t("auth.mfaCodePlaceholder")}
      // priv-2-fix: explicit text color — typed digits must be clearly visible (they were inheriting a
      // muted color). Disabled tint applies ONLY while busy, never during normal typing.
      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-center text-lg font-medium tracking-widest text-ink placeholder:text-slate-400 focus:border-primary focus:outline-none disabled:bg-slate-100 disabled:text-slate-400"
      data-testid="mfa-code-input"
      aria-label={t("auth.mfaCodeLabel")}
    />
  );

  // Recovery codes reveal (shown once, after forced enrollment).
  if (recoveryCodes) {
    return (
      <div className="space-y-4" data-testid="mfa-recovery-codes">
        <h2 className="text-center text-lg font-semibold text-ink">{t("auth.mfaRecoveryTitle")}</h2>
        <p className="text-sm text-slate-600">{t("auth.mfaRecoveryBody")}</p>
        <ul className="grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-4 font-mono text-sm">
          {recoveryCodes.map((c) => (<li key={c} className="text-center">{c}</li>))}
        </ul>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="w-full rounded-lg bg-primary py-2 font-semibold text-white hover:bg-primary-deep"
        >
          {t("auth.mfaRecoveryContinue")}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="mfa-panel">
      <h2 className="text-center text-lg font-semibold text-ink">
        {mode === "enroll" ? t("auth.mfaEnrollTitle") : t("auth.mfaChallengeTitle")}
      </h2>

      {mode === "enroll" && (
        <div className="flex flex-col items-center gap-2 rounded-lg bg-slate-50 p-4 text-sm">
          <p className="text-slate-600">{t("auth.mfaEnrollBody")}</p>
          {enrollSecret ? (
            <>
              {/* QR encodes the otpauth URI (client-side only); the URI is never shown as text. */}
              <div className="rounded bg-white p-2" data-testid="mfa-qr">
                <QRCodeSVG value={enrollSecret.otpauth_uri} size={200} />
              </div>
              <p className="text-center text-slate-600">{t("auth.mfaScanOrManual")}</p>
              <div className="text-xs uppercase tracking-wide text-slate-500">{t("auth.mfaSecretLabel")}</div>
              <code className="block break-all rounded bg-white px-2 py-1 font-mono text-sm" data-testid="mfa-secret">
                {enrollSecret.secret}
              </code>
            </>
          ) : (
            <p className="text-slate-500">{t("common.loading")}</p>
          )}
        </div>
      )}

      <form onSubmit={mode === "enroll" ? onEnrollComplete : onChallenge} className="space-y-3">
        <p className="text-sm text-slate-600">
          {mode === "enroll" ? t("auth.mfaEnrollCodePrompt") : t("auth.mfaChallengePrompt")}
        </p>
        {codeInput}
        {cooldown > 0 ? (
          <p className="text-sm text-error" role="alert" data-testid="mfa-cooldown">
            {t("auth.mfaTooManyAttempts", { seconds: cooldown })}
          </p>
        ) : error ? (
          <p className="text-sm text-error" role="alert">{error}</p>
        ) : null}
        <button
          type="submit"
          disabled={!code || busy}
          className="w-full rounded-lg bg-primary py-2 font-semibold text-white hover:bg-primary-deep disabled:opacity-60"
          data-testid="mfa-submit"
        >
          {mode === "enroll" ? t("auth.mfaEnableAndContinue") : t("auth.mfaVerify")}
        </button>
      </form>

      {/* Cancel is NEVER disabled — the user can always back out. */}
      <button
        type="button"
        onClick={() => { clearPendingMfa(); setCode(""); setEnrollSecret(null); setError(""); setCooldown(0); }}
        className="w-full text-center text-xs text-slate-500 hover:text-slate-700"
        data-testid="mfa-cancel"
      >
        {t("auth.mfaCancel")}
      </button>
    </div>
  );
}

export default MfaPanel;
